import isEmpty from 'lodash/isEmpty';
import upperCase from 'lodash/upperCase';
import React from 'react';
import { Button, ButtonGroup } from 'reactstrap';

import ListAgreements from '../../agreements/ListAgreements';
import { publicStatuses as campaignStatuses } from '../../campaigns/constants';
import ListCampaignsToBeInvoiced from '../../campaigns/ListCampaignsToBeInvoiced';
import ListMyCampaigns from '../../campaigns/ListMyCampaigns';
import SwimlaneCampaigns from '../../campaigns/SwimlaneCampaigns';
import Tabs from '../../generic/Tabs';
import ListCategorizedInvoices from '../../invoices/ListCategorizedInvoices';
import { stages as opportunityStages } from '../../opportunities/constants';
import ListCategorizedOpportunities from '../../opportunities/ListCategorizedOpportunities';
import SwimlaneOpportunities from '../../opportunities/SwimlaneOpportunities';
import { sessionShape } from '../../prop-types';
import withSessionInfo from '../../signin/with-session-info';
import { getPreference, setPreference } from '../../utils/preferences';
import {
  AGREEMENTS,
  CAMPAIGNS,
  DEFAULT_VIEW_MODE,
  INVOICES,
  OPPORTUNITIES,
  VIEW_MODES,
} from '../constants';

function getViewMode(key) {
  return getPreference(`DASHBOARD_VIEW_MODE_${upperCase(key)}`);
}

function setViewMode(key, value) {
  return setPreference(`DASHBOARD_VIEW_MODE_${upperCase(key)}`, value);
}

function tabs(currentViewMode, currentUserId) {
  const opportunitiesView =
    currentViewMode === 'swimlane' ? (
      <SwimlaneOpportunities
        categories={opportunityStages}
        groupBy="stage"
        relevantFor={currentUserId}
      />
    ) : (
      <ListCategorizedOpportunities
        hiddenColumns={['Owner']}
        relevantFor={currentUserId}
        showBudgets
      />
    );

  const campaignsView =
    currentViewMode === 'swimlane' ? (
      <SwimlaneCampaigns
        categories={campaignStatuses}
        groupBy="status"
        relevantFor={currentUserId}
      />
    ) : (
      <ListMyCampaigns relevantFor={currentUserId} />
    );

  const invoicesView = (
    <>
      <div className="mt-4" />
      <h5>Campaigns to be invoiced</h5>
      <ListCampaignsToBeInvoiced ownerId={currentUserId} />

      <div className="mt-4" />
      <ListCategorizedInvoices
        title=""
        ownerId={currentUserId}
        hiddenColumns={['Owner']}
      />
    </>
  );

  const agreementsList = (
    <ListAgreements title="" relevantFor={currentUserId} />
  );

  return [
    {
      label: 'My Agreements',
      value: AGREEMENTS,
      hasSwimlaneViewMode: false,
      content: agreementsList,
    },
    {
      label: 'My Opportunities',
      value: OPPORTUNITIES,
      hasSwimlaneViewMode: true,
      content: opportunitiesView,
    },
    {
      label: 'My Campaigns',
      value: CAMPAIGNS,
      hasSwimlaneViewMode: true,
      content: campaignsView,
    },
    {
      label: 'My Invoices',
      value: INVOICES,
      hasSwimlaneViewMode: false,
      content: invoicesView,
    },
  ];
}

class PCDashboard extends React.PureComponent {
  constructor(props) {
    super(props);

    if (isEmpty(getViewMode(OPPORTUNITIES))) {
      setViewMode(OPPORTUNITIES, DEFAULT_VIEW_MODE.name);
    }
    if (isEmpty(getViewMode(CAMPAIGNS))) {
      setViewMode(CAMPAIGNS, DEFAULT_VIEW_MODE.name);
    }

    this.state = {
      currentViewMode: getViewMode(OPPORTUNITIES),
      tabWithSwimlaneViewModeSelected: true,
      activeTabId: OPPORTUNITIES,
    };

    this.handleViewModeSelect = this.handleViewModeSelect.bind(this);
    this.activeTab = this.activeTab.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
  }

  handleViewModeSelect(event) {
    const { value: currentViewMode } = event.currentTarget;
    this.setViewMode(currentViewMode);
  }

  handleTabSelect(activeTabId) {
    const activeTab = this.activeTab(activeTabId);
    this.setState({
      tabWithSwimlaneViewModeSelected: activeTab.hasSwimlaneViewMode,
      activeTabId: activeTab.value,
      currentViewMode: getViewMode(activeTabId),
    });
  }

  setViewMode(currentViewMode) {
    this.setState({ currentViewMode });
    setViewMode(this.state.activeTabId, currentViewMode);
  }

  activeTab(activeTabId) {
    const { session } = this.props;
    const { currentViewMode } = this.state;
    const allTabs = tabs(currentViewMode, session.user.id);

    return allTabs.find(tab => tab.value === activeTabId);
  }

  isViewModeButtonActive(buttonViewMode) {
    const { currentViewMode, tabWithSwimlaneViewModeSelected } = this.state;

    if (buttonViewMode === 'swimlane') {
      return (
        buttonViewMode === currentViewMode && tabWithSwimlaneViewModeSelected
      );
    }
    return (
      buttonViewMode === currentViewMode || !tabWithSwimlaneViewModeSelected
    );
  }

  render() {
    const { session } = this.props;

    if (!(session && session.user)) return null; // user info not yet loaded

    const { currentViewMode, tabWithSwimlaneViewModeSelected } = this.state;

    return (
      <Tabs
        tabs={tabs(currentViewMode, session.user.id)}
        onTabChange={this.handleTabSelect}
        defaultActive={OPPORTUNITIES}
        id="pcDashboard"
      >
        {tabWithSwimlaneViewModeSelected && (
          <ButtonGroup>
            {VIEW_MODES.map(({ name: buttonViewMode, icon: Icon }) => (
              <Button
                key={buttonViewMode}
                title={`Switch to ${buttonViewMode} view`}
                value={buttonViewMode}
                size="sm"
                outline
                onClick={this.handleViewModeSelect}
                active={this.isViewModeButtonActive(buttonViewMode)}
              >
                <Icon />
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Tabs>
    );
  }
}

PCDashboard.propTypes = {
  session: sessionShape,
};

PCDashboard.defaultProps = {
  session: null,
};

export default withSessionInfo(PCDashboard);
